/**
 * Composant d'alerte de sécurité
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Affiche des alertes de sécurité avec différents niveaux de sévérité.
 */

  // Client Component pour Next.js

import React, { useEffect, useState } from 'react';

/**
 * Type de sévérité de l'alerte
 */
export type AlertSeverity = 'info' | 'success' | 'warning' | 'error';

/**
 * Props du composant SecurityAlert
 */
export interface SecurityAlertProps {
  /** Type de l'alerte */
  severity?: AlertSeverity;
  /** Titre de l'alerte */
  title?: string;
  /** Message de l'alerte */
  message: string;
  /** Afficher le bouton de fermeture */
  closable?: boolean;
  /** Callback de fermeture */
  onClose?: () => void;
  /** Fermeture automatique après X millisecondes (0 = pas de fermeture auto) */
  autoClose?: number;
  /** Afficher l'icône */
  showIcon?: boolean;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Configuration des couleurs et icônes par sévérité
 */
const ALERT_STYLES = {
  info: {
    container: 'bg-accent-light border-accent',
    icon: 'text-accent',
    title: 'text-accent',
    message: 'text-accent',
    closeButton: 'text-accent hover:text-accent',
  },
  success: {
    container: 'bg-success-light border-success',
    icon: 'text-success',
    title: 'text-success',
    message: 'text-success',
    closeButton: 'text-success hover:text-success',
  },
  warning: {
    container: 'bg-warning-light border-warning',
    icon: 'text-warning',
    title: 'text-warning',
    message: 'text-warning',
    closeButton: 'text-warning hover:text-warning',
  },
  error: {
    container: 'bg-danger-light border-danger',
    icon: 'text-danger',
    title: 'text-danger',
    message: 'text-danger',
    closeButton: 'text-danger hover:text-danger',
  },
} as const;

/**
 * Composant SecurityAlert
 *
 * @example
 * ```tsx
 * function SecurityDashboard() {
 *   const { isDetected, reason } = useSuspiciousActivityDetection(attempts);
 *
 *   return (
 *     <div>
 *       {isDetected && (
 *         <SecurityAlert
 *           severity="warning"
 *           title="Activité suspecte détectée"
 *           message={reason}
 *           closable
 *         />
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function SecurityAlert({
  severity = 'info',
  title,
  message,
  closable = false,
  onClose,
  autoClose = 0,
  showIcon = true,
  className = '',
}: SecurityAlertProps) {
  const [isVisible, setIsVisible] = useState(true);
  const styles = ALERT_STYLES[severity];

  // Fermeture automatique
  useEffect(() => {
    if (autoClose > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, autoClose);

      return () => clearTimeout(timer);
    }
  }, [autoClose]);

  /**
   * Gérer la fermeture
   */
  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  /**
   * Obtenir l'icône selon la sévérité
   */
  const getIcon = (): React.ReactElement => {
    switch (severity) {
      case 'success':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
        );
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={`rounded-lg border p-4 ${styles.container} ${className} animate-in fade-in slide-in-from-top-2 duration-300`}
      role="alert"
      aria-live={severity === 'error' ? 'assertive' : 'polite'}
    >
      <div className="flex items-start">
        {/* Icône */}
        {showIcon && (
          <div className={`flex-shrink-0 ${styles.icon}`} aria-hidden="true">
            {getIcon()}
          </div>
        )}

        {/* Contenu */}
        <div className={`flex-1 ${showIcon ? 'ml-3' : ''}`}>
          {title && (
            <h3 className={`text-sm font-semibold ${styles.title} mb-1`}>
              {title}
            </h3>
          )}
          <p className={`text-sm ${styles.message}`}>
            {message}
          </p>
        </div>

        {/* Bouton de fermeture */}
        {closable && (
          <button
            type="button"
            onClick={handleClose}
            className={`flex-shrink-0 ml-3 inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${styles.closeButton}`}
            aria-label="Fermer l'alerte"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Hook pour gérer les alertes de sécurité
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { alert, showAlert, hideAlert } = useSecurityAlert();
 *
 *   const handleAction = () => {
 *     try {
 *       // Action...
 *       showAlert('success', 'Action réussie');
 *     } catch (err) {
 *       showAlert('error', 'Échec de l\'action');
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       {alert && <SecurityAlert {...alert} onClose={hideAlert} />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useSecurityAlert() {
  const [alert, setAlert] = useState<SecurityAlertProps | null>(null);

  const showAlert = (
    severity: AlertSeverity,
    message: string,
    title?: string,
    options?: Partial<SecurityAlertProps>
  ) => {
    setAlert({
      severity,
      message,
      title,
      closable: true,
      ...options,
    });
  };

  const hideAlert = () => {
    setAlert(null);
  };

  return {
    alert,
    showAlert,
    hideAlert,
  };
}
